/**
 * Page Routes Registry
 * Loads and manages dynamic page routes from CMS
 */

import { GetCMSContent, BulkGetCMSContent } from 'aldoo-ra/CMS/api'
import { api, getCurrentUser } from 'aldoo-ra/API'
import { PermissionAPI } from 'aldoo-ra/Permissions/api'

// Cache for page routes
let routesCache = null
let currentCacheKey = null

// Create site API endpoint
const siteAPI = api('site')

/**
 * Fetch all pages from CMS and populate their data
 * Returns all pages - filtering by status happens in buildRouteMap
 * @returns {Promise<Array>} Array of page data with populated layouts and slots
 */
export const fetchPages = async () => {
  try {
    // Fetch all pages regardless of status
    // Permission-based filtering happens in buildRouteMap
    const pagesResponse = await GetCMSContent({
      match: {
        'content.type': 'AldooPage',
      },
      itemsPerPage: 1000,
    })

    if (!pagesResponse.result || !pagesResponse.data) {
      console.warn('Failed to fetch pages from CMS')
      return []
    }

    // Collect all IDs that need to be fetched
    const allLinkIds = []
    const allLayoutIds = []

    pagesResponse.data.forEach(page => {
      // Collect link IDs
      if (page.content?.link && Array.isArray(page.content.link) && page.content.link.length > 0) {
        const linkRef = page.content.link[0]
        if (linkRef._id) allLinkIds.push(linkRef._id)
      }

      // Collect layout/slot IDs
      if (page.content?.layout && Array.isArray(page.content.layout) && page.content.layout.length > 0) {
        page.content.layout.forEach(ref => {
          if (ref._id) allLayoutIds.push(ref._id)
        })
      }
    })

    // Bulk fetch all links
    let linksMap = {}
    if (allLinkIds.length > 0) {
      const linksResponse = await BulkGetCMSContent({
        payload: { ids: allLinkIds, expand: true }
      })
      if (linksResponse.result && linksResponse.data) {
        linksMap = Object.fromEntries(
          linksResponse.data.map(item => [item._id, item.content])
        )
      }
    }

    // Bulk fetch all layout items (could be slots or layouts)
    let layoutItemsMap = {}
    if (allLayoutIds.length > 0) {
      const layoutItemsResponse = await BulkGetCMSContent({
        payload: { ids: allLayoutIds, expand: true }
      })
      if (layoutItemsResponse.result && layoutItemsResponse.data) {
        layoutItemsMap = Object.fromEntries(
          layoutItemsResponse.data.map(item => [item._id, item])
        )
      }
    }

    // Collect all slot and component IDs based on the structure
    const allSlotIds = []
    const allComponentIds = []

    Object.values(layoutItemsMap).forEach(item => {
      const content = item.content
      // Check if this is a slot
      const isSlot = content?.type === 'AldooLayoutSlot' ||
                    (content?.id && content?.component && content?.position && content?.size)

      if (isSlot) {
        // Extract component ID from slot
        if (content?.component && Array.isArray(content.component) && content.component.length > 0) {
          const compRef = content.component[0]
          const compId = compRef._id || compRef
          if (compId) allComponentIds.push(compId)
        }
      } else {
        // This is a layout, collect its slot IDs
        if (content?.slots && Array.isArray(content.slots) && content.slots.length > 0) {
          content.slots.forEach(slotRef => {
            if (slotRef._id) allSlotIds.push(slotRef._id)
          })
        }
      }
    })

    // Bulk fetch all slots (for old structure)
    let slotsMap = {}
    if (allSlotIds.length > 0) {
      const slotsResponse = await BulkGetCMSContent({
        payload: { ids: allSlotIds, expand: true }
      })
      if (slotsResponse.result && slotsResponse.data) {
        slotsMap = Object.fromEntries(
          slotsResponse.data.map(item => [item._id, item])
        )

        // Collect component IDs from these slots
        slotsResponse.data.forEach(slot => {
          if (slot.content?.component && Array.isArray(slot.content.component) && slot.content.component.length > 0) {
            const compRef = slot.content.component[0]
            const compId = compRef._id || compRef
            if (compId) allComponentIds.push(compId)
          }
        })
      }
    }

    // Bulk fetch all components
    let componentsMap = {}
    if (allComponentIds.length > 0) {
      const componentsResponse = await BulkGetCMSContent({
        payload: { ids: [...new Set(allComponentIds)], expand: true }
      })
      if (componentsResponse.result && componentsResponse.data) {
        componentsMap = Object.fromEntries(
          componentsResponse.data.map(item => [item._id, item.content])
        )
      }
    }

    // Now populate all pages using the fetched data
    const populatedPages = pagesResponse.data.map(page => {
      try {
        // Populate link
        if (page.content?.link && Array.isArray(page.content.link) && page.content.link.length > 0) {
          const linkRef = page.content.link[0]
          const linkContent = linksMap[linkRef._id]
          if (linkContent) {
            page.content.link = [{
              ...linkRef,
              content: linkContent,
            }]
          }
        }

        // Populate layout
        if (page.content?.layout && Array.isArray(page.content.layout) && page.content.layout.length > 0) {
          const firstRef = page.content.layout[0]
          const firstItem = layoutItemsMap[firstRef._id]

          if (firstItem) {
            const content = firstItem.content
            const isSlotBased = content?.type === 'AldooLayoutSlot' ||
                               (content?.id && content?.component && content?.position && content?.size)

            if (isSlotBased) {
              // New structure: Page → Slots directly
              page.content.layout = page.content.layout.map(slotRef => {
                const slot = layoutItemsMap[slotRef._id]
                if (!slot) return slotRef

                const slotContent = { ...slot.content }

                // Populate component
                if (slotContent.component && Array.isArray(slotContent.component) && slotContent.component.length > 0) {
                  const componentRef = slotContent.component[0]
                  const componentId = componentRef._id || componentRef
                  const fullComponent = componentsMap[componentId]

                  if (fullComponent) {
                    slotContent.component = [{
                      ...componentRef,
                      _id: componentId,
                      content: fullComponent
                    }]
                  }
                }

                return {
                  ...slotRef,
                  content: slotContent,
                }
              })
            } else {
              // Old structure: Page → Layouts → Slots
              page.content.layout = page.content.layout.map(layoutRef => {
                const layout = layoutItemsMap[layoutRef._id]
                if (!layout) return layoutRef

                const layoutContent = { ...layout.content }

                // Populate slots
                if (layoutContent.slots && Array.isArray(layoutContent.slots) && layoutContent.slots.length > 0) {
                  layoutContent.slots = layoutContent.slots.map(slotRef => {
                    const slot = slotsMap[slotRef._id]
                    if (!slot) return slotRef

                    const slotContent = { ...slot.content }

                    // Populate component
                    if (slotContent.component && Array.isArray(slotContent.component) && slotContent.component.length > 0) {
                      const componentRef = slotContent.component[0]
                      const componentId = componentRef._id || componentRef
                      const fullComponent = componentsMap[componentId]

                      if (fullComponent) {
                        slotContent.component = [{
                          ...componentRef,
                          _id: componentId,
                          content: fullComponent
                        }]
                      }
                    }

                    return {
                      ...slotRef,
                      content: slotContent,
                    }
                  })
                }

                return {
                  ...layoutRef,
                  content: layoutContent,
                }
              })
            }
          }
        }

        return page
      } catch (error) {
        console.error(`Failed to populate page ${page._id}:`, error)
        return page
      }
    })

    return populatedPages
  } catch (error) {
    console.error('Error fetching pages:', error)
    return []
  }
}

/**
 * Build route map from pages data
 * Includes all pages - permission checking happens in the renderer
 * @param {Array} pages - Array of page data
 * @returns {Object} Map of URL paths to page data
 */
export const buildRouteMap = (pages) => {
  const routeMap = {}

  // Don't filter by permissions here - let the renderer handle it
  // This ensures routes exist for SSR, and permission checks happen client-side

  pages.forEach((page) => {
    try {
      const pageContent = page.content || page
      const status = pageContent.status?.value || pageContent.status

      // Extract URL from link or slug field
      const link = pageContent.link?.[0]
      const linkContent = link?.content || link
      let url = linkContent?.url || pageContent.slug

      if (!url) {
        console.warn(`Page "${pageContent.name}" has no URL configured`, page)
        return
      }

      // Normalize URL (ensure it starts with /)
      if (!url.startsWith('/')) {
        url = '/' + url
      }

      // Remove trailing slash (except for root)
      if (url.length > 1 && url.endsWith('/')) {
        url = url.slice(0, -1)
      }

      routeMap[url] = page
    } catch (error) {
      console.error('Error processing page for route map:', error, page)
    }
  })

  return routeMap
}

/**
 * Load all page routes with caching
 * @param {boolean} forceRefresh - Force refresh cache
 * @returns {Promise<Object>} Map of URL paths to page data
 */
export const loadPageRoutes = async (forceRefresh = false) => {
  // Get current cache key from site API
  const cacheResponse = await siteAPI({ cache: {} })
  const newCacheKey = cacheResponse?.pages

  // Return cached routes if cache key hasn't changed
  if (!forceRefresh && routesCache && currentCacheKey === newCacheKey) {
    return routesCache
  }

  // Fetch and build routes
  const pages = await fetchPages()
  const routes = buildRouteMap(pages)

  // Update cache
  routesCache = routes
  currentCacheKey = newCacheKey

  return routes
}

/**
 * Get page data for a specific URL
 * @param {string} url - The URL path
 * @returns {Promise<Object|null>} Page data or null if not found
 */
export const getPageByUrl = async (url) => {
  const routes = await loadPageRoutes()

  // Normalize URL
  if (!url.startsWith('/')) {
    url = '/' + url
  }
  if (url.length > 1 && url.endsWith('/')) {
    url = url.slice(0, -1)
  }

  return routes[url] || null
}

/**
 * Clear the routes cache
 */
export const clearRoutesCache = () => {
  routesCache = null
  currentCacheKey = null
}

/**
 * Get all cached routes
 * @returns {Object|null} Cached routes or null
 */
export const getCachedRoutes = () => {
  return routesCache
}

export default {
  fetchPages,
  buildRouteMap,
  loadPageRoutes,
  getPageByUrl,
  clearRoutesCache,
  getCachedRoutes,
}